/**
 * @file: useTaskTimelineModels.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { MediaObject } from '@euler/model';
import { TextLine } from '@euler/model/AttributedText';
import { ReactNode, useMemo } from 'react';
import { StyleProp, TextStyle } from 'react-native';
import { TimelineSection, TimelineSectionType } from './types';

export type TimelineEventCell = {
  type: 'event';
  id: string;
  technicianName: string;
  title: TextLine;
  taglines?: TextLine[];
  timestamp: Date;
  medias?: MediaObject[];
  first: boolean;
  last: boolean;
  lastSection: boolean;
  sectionType: TimelineSectionType;
  extra?: ReactNode;
};

export type TimelineEventSectionHeaderCell = {
  type: 'section-header';
  id: string;
  sectionType: TimelineSectionType;
  title: string;
  linkText?: string;
  linkTextStyle?: StyleProp<TextStyle>;
  onPressLink?: (type: TimelineSectionType) => void;
  first: boolean;
  last: boolean;
};

export type TimelineCell = TimelineEventSectionHeaderCell | TimelineEventCell;

export function useTaskTimelineModels({
  sections,
  linkTextStyle,
  onPressSectionHeader,
}: {
  sections: TimelineSection[];
  linkTextStyle?: StyleProp<TextStyle>;
  onPressSectionHeader?: (type: TimelineSectionType) => void;
}) {
  return useMemo(() => {
    const cells: TimelineCell[] = [];
    for (const section of sections) {
      cells.push({
        id: `section-header/${section.type}`,
        type: 'section-header',
        sectionType: section.type,
        title: section.label,
        linkText: section.type === 'quotation' ? '查看报价/结算单' : undefined,
        onPressLink:
          section.type === 'order-close' ||
          section.type === 'order-rollback' ||
          section.type === 'order' ||
          section.type === 'order-update'
            ? undefined
            : onPressSectionHeader,
        first: section === sections[0],
        last: section === sections[sections.length - 1],
        linkTextStyle,
      });
      for (const item of section.items) {
        cells.push({
          id: item.key,
          type: 'event',
          sectionType: section.type,
          technicianName: item.author,
          title: item.label,
          taglines: item.taglines,
          timestamp: item.timestamp,
          extra: item.extra,
          first: item === section.items[0],
          last: item === section.items[section.items.length - 1],
          lastSection: section === sections[sections.length - 1],
          medias: item.medias?.map(x => ({
            type:
              x.type == null || x.type.startsWith('image/') ? 'image' : 'video',
            id: x.id,
            url: x.url,
            coverUrl: x.coverUrl ?? undefined,
          })),
        });
      }
    }
    return { cells };
  }, [sections, onPressSectionHeader, linkTextStyle]);
}
